import { useState, useEffect } from 'react';
import apiService from '../config';
import KutumbForm from './KutumbForm';
import { useLanguage } from '../StoreContext/LanguageContext';
import { useSignature } from '../DigitalSignature/UseSignatue';
import SignaturePad from '../DigitalSignature/SignaturePad';

const FamilySurveyForm = () => {
  const { language, translations } = useLanguage();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);



  // Fetch projectId similar to Section15
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return {
            id: projectData.project_id || projectData.id,
            name: projectData.project_name || projectData.name
          };
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }

      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return {
            id: project.project_id || project.id,
            name: project.project_name || project.name
          };
        } catch (e) {
          console.error('Error parsing selected project:', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          return {
            id: user.data?.project_id || user.project_id,
            name: user.data?.project_name || user.project_name || 'Unknown Project'
          };
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      return { id: null, name: null };
    };

    const projectInfo = getProjectInfo();
    setProjectId(projectInfo.id);
    console.log('Current Project Info:', projectInfo);
  }, []);

  // Fetch proposalId
  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      if (currentProposal) {
        try {
          return {
            proposalId:
              currentProposal?.data?.default_proposal ||
              currentProposal?.data?.proposal_id ||
              currentProposal?.default_proposal ||
              currentProposal?.proposal_id ||
              null,
          };
        } catch (e) {
          console.error('Error parsing proposal info:', e);
        }
      }
      return { proposalId: null };
    };

    const proposalInfo = getProposalInfo();
    setProposalId(proposalInfo.proposalId);
  }, []);

  // Fetch family details
  useEffect(() => {
    if (projectId && proposalId) {
      fetchFamilyDetails();
    }
  }, [projectId, proposalId]);

  

  const fetchFamilyDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.getFamilyDetailsByProject(projectId, proposalId);

      if (response.status === 200 && response.data) {
        const transformedData = response.data.map((item, index) => ({
          srno: index + 1,
          ownerId: item.land_owner_id,
          gatno: item.survey_no,
          ownername: item.owner_name,
          villageName: item.village_name,
        }));
        setData(transformedData);
      }
    } catch (err) {
      console.error('Error fetching family details:', err);
      setError('Failed to load family details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ownerId) => {
    setSelectedOwnerId(ownerId);
  };

  const handleBack = () => {
    setSelectedOwnerId(null);
    fetchFamilyDetails();
  };

  // 🔹 If an owner is selected, show KutumbForm directly
  if (selectedOwnerId) {
    return <KutumbForm ownerId={selectedOwnerId} onBack={handleBack} />;
  }

  if (loading) {
    return (
      <div className="p-8 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading family details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchFamilyDetails}
              className="mt-2 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Owner Information</h1>
          

          {/* <button
            onClick={fetchFamilyDetails}
            className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Refresh
          </button> */}
        </div>

        {data.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600">No family details found for this project.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200 overflow-x-auto">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Sr No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">House No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Owner Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Village</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row) => (
                  <tr key={row.srno} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.srno}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.gatno}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.ownername}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.villageName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEdit(row.ownerId)}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


    </div>
  );
};

export default FamilySurveyForm;

